/**
 * React Query hook for registration campaign
 *
 * Provides data fetching and mutation for tenant registration campaign configuration
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RegistrationCampaignApiService } from '../services/registrationCampaignApi';
import type {
  RegistrationCampaignStatus,
  RegistrationCampaignConfig,
} from '../types/registrationCampaign';

/**
 * Query key factory for registration campaign
 */
export const registrationCampaignQueryKey = (tenantId: string) =>
  ['tenant', tenantId, 'registrationCampaign'] as const;

/**
 * Hook return type for useRegistrationCampaign
 */
export interface UseRegistrationCampaignResult {
  /** Registration campaign status */
  status: RegistrationCampaignStatus | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Error from fetching status */
  error: Error | null;
  /** Refetch status */
  refetch: () => void;
  /** Function to update campaign configuration */
  updateCampaign: (config: RegistrationCampaignConfig) => Promise<RegistrationCampaignStatus>;
  /** Loading state for update mutation */
  isUpdating: boolean;
  /** Error from update mutation */
  updateError: Error | null;
}

/**
 * React Query hook for managing registration campaign
 *
 * @param tenantId - Tenant ID to fetch registration campaign for
 * @returns Registration campaign status, loading states, and mutation functions
 */
export function useRegistrationCampaign(
  tenantId: string
): UseRegistrationCampaignResult {
  const queryClient = useQueryClient();

  // Query for fetching registration campaign status
  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: registrationCampaignQueryKey(tenantId),
    queryFn: () => RegistrationCampaignApiService.getRegistrationCampaignStatus(tenantId),
  });

  // Mutation for updating registration campaign
  const {
    mutateAsync: updateCampaign,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (config: RegistrationCampaignConfig) =>
      RegistrationCampaignApiService.configureRegistrationCampaign(tenantId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: registrationCampaignQueryKey(tenantId),
      });
    },
  });

  return {
    status,
    isLoading,
    error: error as Error | null,
    refetch,
    updateCampaign,
    isUpdating,
    updateError: updateError as Error | null,
  };
}
